import { useCallback, useEffect, useState } from 'react'
import { Skull, RefreshCw, Globe, Briefcase, TrendingUp, ExternalLink, X as XIcon } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface Victim {
  victim: string
  group: string
  country: string
  sector: string
  description: string
  discovered: string | null
  url: string
  website: string
}

interface RansomwareData {
  victims: Victim[]
  top_groups: { name: string; victims: number }[]
  top_countries: { country: string; victims: number }[]
  top_sectors: { sector: string; victims: number }[]
  daily_trend: { date: string; count: number }[]
  total_recent: number
  window_days: number
  active_groups: number
}

function relative(ts: string | null): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }) } catch { return ts }
}

function TrendSparkline({ points }: { points: { date: string; count: number }[] }) {
  if (!points.length) return null
  const maxV = Math.max(1, ...points.map(p => p.count))
  const w = 320
  const h = 60
  const stepX = points.length > 1 ? w / (points.length - 1) : w
  const path = points.map((p, i) => {
    const x = i * stepX
    const y = h - (p.count / maxV) * (h - 6) - 3
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
  const peak = points.reduce((m, p) => (p.count > m.count ? p : m), points[0])

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14">
      <defs>
        <linearGradient id="ransom-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L${(points.length - 1) * stepX},${h} L0,${h} Z`} fill="url(#ransom-fill)" />
      <path d={path} fill="none" stroke="#fb7185" strokeWidth="1.5" strokeLinecap="round" />
      <title>Peak: {peak.count} victims on {peak.date}</title>
    </svg>
  )
}

export default function RansomwarePage() {
  const [data, setData] = useState<RansomwareData | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(30)
  const [country, setCountry] = useState<string | null>(null)
  const [sector, setSector]   = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/cyber/ransomware', { params: { days, limit: 150 } })
      setData(r.data)
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 300_000); return () => clearInterval(i) }, [fetchData])

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Skull size={24} className="text-rose" /> Ransomware Leak-Site Tracker
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Live victim posts from ransomware gangs · data: <a href="https://ransomware.live" target="_blank" rel="noopener" className="text-blue-light hover:underline">ransomware.live</a>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0 rounded-md border border-border overflow-hidden text-[10px]">
            {[7, 14, 30, 90, 180].map(d => (
              <button key={d} type="button" onClick={() => setDays(d)}
                className={clsx('px-2 py-1', days === d ? 'bg-rose/20 text-rose-light font-semibold' : 'text-text-muted hover:bg-surface-hover')}>
                {d}d
              </button>
            ))}
          </div>
          <button type="button" onClick={fetchData} className="btn-secondary text-xs" title="Refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card p-4">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Victims ({data.window_days}d)</div>
            <div className="text-3xl font-bold text-rose-light mt-1">{data.total_recent}</div>
          </div>
          <div className="card p-4">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Active gangs</div>
            <div className="text-3xl font-bold text-text-primary mt-1">{data.active_groups}</div>
          </div>
          <div className="card p-4">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Countries hit</div>
            <div className="text-3xl font-bold text-text-primary mt-1">{data.top_countries.length}</div>
          </div>
          <div className="card p-4">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Peak day</div>
            <div className="text-3xl font-bold text-text-primary mt-1">
              {data.daily_trend.length ? Math.max(...data.daily_trend.map(d => d.count)) : 0}
              <span className="text-[11px] text-text-muted font-normal ml-1">victims</span>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="card p-4 lg:col-span-2">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
              <TrendingUp size={14} className="text-rose" /> Daily victim count · last {data.window_days} days
            </h2>
            <TrendSparkline points={data.daily_trend} />
            <div className="flex justify-between text-[10px] text-text-muted mt-1">
              {data.daily_trend.length > 0 && (
                <>
                  <span>{data.daily_trend[0].date}</span>
                  <span>{data.daily_trend[data.daily_trend.length - 1].date}</span>
                </>
              )}
            </div>
          </div>

          <div className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
              <Skull size={14} className="text-rose" /> Top gangs
            </h2>
            <div className="space-y-1.5">
              {data.top_groups.slice(0, 10).map(g => {
                const pct = (g.victims / (data.top_groups[0]?.victims || 1)) * 100
                return (
                  <div key={g.name}>
                    <div className="flex justify-between text-xs text-text-secondary">
                      <span className="font-mono">{g.name}</span>
                      <span>{g.victims}</span>
                    </div>
                    <div className="w-full h-1 bg-surface-hover rounded-full overflow-hidden">
                      <div className="h-full bg-rose" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {data && (() => {
        const filtered = data.victims.filter(v => {
          if (country && (v.country || '').toUpperCase() !== country.toUpperCase()) return false
          if (sector && (v.sector || '').toLowerCase() !== sector.toLowerCase()) return false
          return true
        })
        return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
              <Globe size={14} className="text-amber" /> Top countries
              <span className="text-[10px] text-text-muted font-normal ml-auto">click to filter</span>
            </h2>
            <div className="space-y-1">
              {data.top_countries.slice(0, 12).map(c => {
                const active = country === c.country
                return (
                  <button
                    key={c.country}
                    type="button"
                    onClick={() => setCountry(active ? null : c.country)}
                    className={clsx(
                      'w-full flex justify-between text-xs px-1.5 py-1 rounded transition-colors',
                      active ? 'bg-amber/20 text-amber-light font-semibold' : 'hover:bg-surface-hover',
                    )}
                  >
                    <span className={clsx('font-mono', active ? 'text-amber-light' : 'text-text-secondary')}>{c.country}</span>
                    <span className={clsx('font-semibold', active ? 'text-amber-light' : 'text-text-primary')}>{c.victims}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
              <Briefcase size={14} className="text-emerald" /> Top sectors
              <span className="text-[10px] text-text-muted font-normal ml-auto">click to filter</span>
            </h2>
            <div className="space-y-1">
              {data.top_sectors.slice(0, 12).map(s => {
                const active = sector === s.sector
                const label = s.sector || 'unknown'
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setSector(active ? null : s.sector || null)}
                    className={clsx(
                      'w-full flex justify-between text-xs px-1.5 py-1 rounded transition-colors',
                      active ? 'bg-emerald/20 text-emerald-light font-semibold' : 'hover:bg-surface-hover',
                    )}
                  >
                    <span className={active ? 'text-emerald-light' : 'text-text-secondary'}>{label}</span>
                    <span className={clsx('font-semibold', active ? 'text-emerald-light' : 'text-text-primary')}>{s.victims}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                <Skull size={14} className="text-rose" />
                {country || sector ? 'Filtered victims' : 'Latest victims'}
              </h2>
              <span className="text-[10px] text-text-muted">{filtered.length} / {data.victims.length}</span>
            </div>

            {(country || sector) && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {country && (
                  <button type="button" onClick={() => setCountry(null)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber/20 text-amber-light text-[10px] hover:bg-amber/30">
                    <Globe size={9} /> {country.toUpperCase()} <XIcon size={9} />
                  </button>
                )}
                {sector && (
                  <button type="button" onClick={() => setSector(null)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald/20 text-emerald-light text-[10px] hover:bg-emerald/30">
                    <Briefcase size={9} /> {sector} <XIcon size={9} />
                  </button>
                )}
              </div>
            )}

            <div className="space-y-2 max-h-[380px] overflow-y-auto">
              {filtered.slice(0, 50).map((v, i) => (
                <a key={i} href={v.url} target="_blank" rel="noopener" className="block p-2 rounded border border-border hover:bg-surface-hover transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-rose-light">{v.group}</span>
                    <span className="text-[10px] text-text-muted">{relative(v.discovered)}</span>
                  </div>
                  <div className="text-xs text-text-primary font-medium mt-0.5 flex items-center gap-1">
                    {v.victim}
                    {v.url && <ExternalLink size={9} className="text-text-muted" />}
                  </div>
                  <div className="text-[10px] text-text-muted mt-0.5 flex items-center gap-2">
                    {v.country && (
                      <button type="button" onClick={e => { e.preventDefault(); setCountry(country === v.country ? null : v.country) }}
                        className="hover:text-amber-light font-medium">{v.country.toUpperCase()}</button>
                    )}
                    {v.sector && (
                      <>
                        <span>·</span>
                        <button type="button" onClick={e => { e.preventDefault(); setSector(sector === v.sector ? null : v.sector) }}
                          className="hover:text-emerald-light">{v.sector}</button>
                      </>
                    )}
                  </div>
                </a>
              ))}
              {filtered.length === 0 && (
                <p className="text-[11px] text-text-muted italic text-center py-6">
                  No victims match the current filter.
                </p>
              )}
            </div>
          </div>
        </div>
        )
      })()}
    </div>
  )
}
